/**
 * La tapette : au verdict, la main gantée du gagnant gifle le perdant (cartoon).
 * Chronologie pure, en fractions du cadre : la main arrive de hors-champ, frappe
 * la joue, le cadre entier tremble, « PAF ! », puis la main repart.
 */
import type { Phase } from '../game/stateMachine';

export const SLAP_MS = 1500;
export const IMPACT_MS = 500;
const SHAKE_MS = 500;
const LEAVE_AT = 1000;

const OFF = 1.35; // hors du cadre, à droite
const CHEEK = { x: 0.62, y: 0.52 };

export interface SlapFrame {
  /** Position de la main (fractions du cadre) et inclinaison (rad) ; null : hors champ. */
  readonly hand: { x: number; y: number; angle: number } | null;
  /** Tremblement du cadre entier : rotation (degrés), décalage (fraction de largeur). */
  readonly shake: { rotate: number; dx: number };
  /** Opacité du « PAF ! » (0 → 1). */
  readonly paf: number;
  readonly done: boolean;
}

const NONE = { rotate: 0, dx: 0 };
const lerp = (a: number, b: number, k: number): number => a + (b - a) * k;

/** Où joue la tapette : chez le gagnant sur l'image de l'ami, chez le perdant sur la sienne. */
export function slapSide(phase: Phase): 'remote' | 'local' | null {
  if (phase.name !== 'result') return null;
  if (phase.outcome === 'won') return 'remote';
  if (phase.outcome === 'lost') return 'local';
  return null;
}

export function slapFrame(t: number): SlapFrame {
  if (t < 0 || t >= SLAP_MS) return { hand: null, shake: NONE, paf: 0, done: t >= SLAP_MS };

  let hand: SlapFrame['hand'];
  if (t < IMPACT_MS) {
    const k = (t / IMPACT_MS) ** 2; // accélère jusqu'à l'impact
    hand = { x: lerp(OFF, CHEEK.x, k), y: lerp(0.45, CHEEK.y, k), angle: lerp(-0.45, 0, k) };
  } else if (t < LEAVE_AT) {
    hand = { ...CHEEK, angle: 0 };
  } else {
    const k = (t - LEAVE_AT) / (SLAP_MS - LEAVE_AT);
    const e = k * k * (3 - 2 * k);
    hand = { x: lerp(CHEEK.x, OFF, e), y: lerp(CHEEK.y, 0.45, e), angle: lerp(0, 0.35, e) };
  }

  const d = t - IMPACT_MS;
  let shake = NONE;
  if (d >= 0 && d < SHAKE_MS) {
    const amp = Math.exp(-d / 110);
    const phase = (2 * Math.PI * d) / 90;
    shake = { rotate: 9 * amp * Math.cos(phase), dx: 0.03 * amp * Math.sin(phase) };
  }
  const paf = d < 0 ? 0 : d < 500 ? 1 : Math.max(0, 1 - (d - 500) / 450);

  return { hand, shake, paf, done: false };
}
